import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import React, { FC } from 'react';
import {
  getCurrencySymbol,
  getFullPlanObj,
  perCycleAmountBasedOnDuration,
  formatIntervalDisplay,
} from 'src/lib/utils';
import { Plan } from 'src/pages/deprecated/payment/types';
import { useAppSelector } from 'src/store/hooks';
import colors from 'src/styles/equalution/colors';
import Fonts from 'src/styles/equalution/fonts';
import PlanBadge from './PlanBadge';

interface PlanOptionButtonProps {
  plan: Plan;
  onPlanSelect?: (id: string) => void;
  isSelected?: boolean;
}

const PlanOptionButton: FC<PlanOptionButtonProps> = ({
  plan,
  onPlanSelect = () => {},
  isSelected,
}) => {
  const { availablePlans, addonWeek, couponObj } = useAppSelector(
    state => state.signUp
  );
  const finalPlanObj = getFullPlanObj(availablePlans, plan.id, addonWeek);

  if (!finalPlanObj) {
    return null;
  }

  const intervalString = formatIntervalDisplay(
    finalPlanObj.intervalCount,
    finalPlanObj.interval
  );

  // Determine badge
  const isSixMonth =
    finalPlanObj.intervalCount === 6 && finalPlanObj.interval === 'month';
  const isAnnual =
    finalPlanObj.intervalCount === 12 && finalPlanObj.interval === 'month';

  // Calculate monthly price
  const monthlyPrice = perCycleAmountBasedOnDuration(
    finalPlanObj.perCycleAmount,
    intervalString
  );

  // Calculate total billing amount
  const shouldTextChange: boolean =
    couponObj && couponObj?.duration?.toLowerCase() === 'once';
  const totalBillingAmount = shouldTextChange
    ? (finalPlanObj.unitAmountCents / 100).toFixed(2)
    : parseFloat(finalPlanObj.unitAmount || '0').toFixed(2);

  // Format billing frequency text
  const getBillingFrequencyText = () => {
    if (isAnnual) {
      return 'Billed annually';
    }
    if (finalPlanObj.intervalCount === 1 && finalPlanObj.interval === 'month') {
      return 'Billed monthly';
    }
    return `Billed every ${finalPlanObj.intervalCount} ${finalPlanObj.interval}s`;
  };

  return (
    <Box
      display="flex"
      alignItems={'center'}
      justifyContent={'space-between'}
      flex={1}
      paddingX="16px"
      paddingY="8px"
      borderRadius="20px"
      onClick={() => onPlanSelect(plan.id)}
      sx={{
        minHeight: '80px',
        cursor: 'pointer',
        border: isSelected
          ? `2px solid ${colors.SALAD}`
          : `2px solid transparent`,
        transitionDuration: '0.3s',
        backgroundColor: isSelected ? `${colors.SALAD}1A` : colors.CREAM_HALF,
      }}>
      <Box display="flex" flexDirection="column" flex={1}>
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <Typography
            sx={{
              fontFamily: Fonts.FontType.regular,
              fontSize: '20px',
              color: colors.SALAD,
              fontWeight: 700,
            }}>
            {getCurrencySymbol()}
            {monthlyPrice}/month
          </Typography>
          <Typography
            sx={{
              fontFamily: Fonts.FontType.regular,
              fontSize: '20px',
              color: colors.OFF_BLACK,
              fontWeight: 700,
            }}>
            - {intervalString}
          </Typography>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          mb={isSixMonth || isAnnual ? 0.5 : 0}>
          <Typography
            sx={{
              fontFamily: Fonts.FontType.regular,
              fontSize: '14px',
              color: colors.OFF_BLACK,
              fontWeight: 400,
            }}>
            {getCurrencySymbol()}
            {totalBillingAmount}
          </Typography>
          <Typography
            sx={{
              fontFamily: Fonts.FontType.regular,
              fontSize: '14px',
              color: colors.BLUEBERRY,
              fontWeight: 400,
            }}>
            {getBillingFrequencyText()}
          </Typography>
        </Box>
        {(isSixMonth || isAnnual) && (
          <PlanBadge type={isSixMonth ? 'Popular' : 'Best value'} />
        )}
      </Box>
      <Box display="flex" alignItems="center" justifyContent="center" ml={2}>
        {isSelected ? (
          <CheckCircleIcon
            sx={{
              color: colors.SALAD,
              fontSize: '24px',
            }}
          />
        ) : (
          <RadioButtonUncheckedIcon
            sx={{
              color: colors.BLUEBERRY_HALF,
              fontSize: '24px',
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default PlanOptionButton;
